import { useState } from "react";

import { completeMission } from "../game";

/**
 * The closed-loop governance demo, told as a numbered story instead of a wall of windows.
 *
 * The earlier rendering listed every monitoring window as an equal card, which buried the
 * one rule that makes the demo make sense: ONE bad reading is watched, TWO in a row cause
 * the shutdown. This version derives five story steps from the same server response and
 * gives each step one bold sentence, so a first-time visitor can follow the plot:
 * normal -> first warning (still running!) -> second strike (stopped) -> attacker gives up
 * (still stopped) -> human turns it back on.
 *
 * All numbers still come from POST /v1/demo/governance/attack - the real governor class
 * run server-side against a session-isolated sandbox. Nothing here is invented in the UI.
 */

interface SandboxWindow {
  index: number;
  phase: string;
  auto_approve_share: number | null;
  step_up_share: number | null;
  psi: number | null;
  state: string;
  reason: string | null;
  warning: boolean;
}

interface SandboxStory {
  synthetic: boolean;
  note: string;
  window_transactions: number;
  certified_max_auto_approve_share: number;
  consecutive_windows_required: number;
  windows: SandboxWindow[];
  demoted_at_window: number | null;
  recovered: boolean;
}

interface StoryStep {
  icon: string;
  title: string;
  detail: string;
  metric: string | null;
  status: "ok" | "warn" | "stopped" | "human";
}

function per100(share: number | null): string {
  return share === null ? "—" : `${Math.round(share * 100)}건`;
}

/** Turn the raw window list into five plot points a first-time visitor can follow. */
function deriveSteps(story: SandboxStory): StoryStep[] {
  const cap = Math.round(story.certified_max_auto_approve_share * 100);
  const control = story.windows.filter((w) => w.phase === "control");
  const attack = story.windows.filter((w) => w.phase === "attack");
  const recovered = story.windows.find((w) => w.phase === "recovered");

  const controlShare =
    control.length > 0 ? control[control.length - 1].auto_approve_share : null;
  const firstAttack = attack[0] ?? null;
  const strikeWindow = attack.find((w) => w.state === "DEMOTED_SAFE") ?? null;

  const steps: StoryStep[] = [
    {
      icon: "😌",
      title: "평소: 100건 중 자동 통과는 약 " + per100(controlShare),
      detail:
        `안전 검사에서 미리 정해둔 허용선은 100건 중 ${cap}건이에요. 평소엔 그보다 한참 ` +
        "아래라 자동화가 정상 작동합니다.",
      metric: `자동 통과 ${per100(controlShare)} / 허용선 ${cap}건`,
      status: "ok",
    },
  ];

  if (firstAttack) {
    steps.push({
      icon: "🎭",
      title: `공격 시작: 갑자기 ${per100(firstAttack.auto_approve_share)}이 자동 통과`,
      detail:
        "사기꾼이 거래를 평범해 보이게 위장해서 대량으로 밀어넣자, 자동 통과가 허용선을 " +
        "훌쩍 넘었어요. 하지만 한 번은 우연일 수도 있으니 시스템은 아직 멈추지 않고 " +
        "지켜봅니다. (오작동으로 멀쩡한 은행을 세우면 그것도 사고니까요)",
      metric: `허용선 ${cap}건 초과 — 1번째 ⚠️`,
      status: "warn",
    });
  }
  if (strikeWindow) {
    steps.push({
      icon: "🛑",
      title: "2번 연속 초과: 그 자리에서 자동화 정지",
      detail:
        `또 ${per100(strikeWindow.auto_approve_share)}. 우연이 아니라는 뜻이죠. 시스템이 ` +
        "스스로 자동 판단 권한을 반납하고, 이제 모든 애매한 거래는 사람이 봅니다. " +
        "사기꾼 입장에선 위장 공격이 통하지 않게 된 거예요.",
      metric: `허용선 ${cap}건 초과 — 2번 연속 → 정지`,
      status: "stopped",
    });
  }
  steps.push({
    icon: "🤫",
    title: "공격이 멈춰도, 자동화는 꺼진 그대로",
    detail:
      "사기꾼이 '들켰나?' 하고 잠잠해져도 시스템은 혼자 다시 켜지지 않아요. 몰래 쉬었다가 " +
      "다시 공격하는 수법에 당하지 않기 위해, 일부러 그렇게 만들었습니다.",
    metric: null,
    status: "stopped",
  });
  steps.push({
    icon: "🧑‍💼",
    title: "다시 켜는 방법은 단 하나: 사람의 승인",
    detail:
      recovered
        ? `담당자가 상황을 확인하고 승인하면 그때 자동화가 복구됩니다. 복구 후 자동 통과는 ${per100(recovered.auto_approve_share)} — 평소 수준으로 돌아왔어요.`
        : "담당자가 상황을 확인하고 승인하면 그때 자동화가 복구됩니다.",
    metric: null,
    status: "human",
  });
  return steps;
}

export function AutonomySandbox() {
  const [story, setStory] = useState<SandboxStory | null>(null);
  const [showRecovery, setShowRecovery] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function attack() {
    setRunning(true);
    setError(null);
    setStory(null);
    setShowRecovery(false);
    try {
      const response = await fetch("/v1/demo/governance/attack", { method: "POST" });
      if (!response.ok) {
        setError(
          response.status === 404 || response.status === 405
            ? "이 배포에서는 아직 켜져 있지 않은 기능입니다."
            : "공격 시뮬레이션을 실행하지 못했습니다.",
        );
        return;
      }
      setStory((await response.json()) as SandboxStory);
      // The attack really ran against the real governor: mission 2 verified.
      completeMission("try-attack");
    } catch {
      setError("공격 시뮬레이션을 실행하지 못했습니다.");
    } finally {
      setRunning(false);
    }
  }

  const steps = story ? deriveSteps(story) : [];
  // The human step stays hidden behind the button so the visitor performs it themselves.
  const visibleSteps = showRecovery ? steps : steps.filter((s) => s.status !== "human");

  return (
    <section className="card">
      <h3>🥊 AI를 공격해서 멈추게 해보기</h3>
      <p>
        이 시스템의 마지막 규칙: <strong>AI는 안전이 확인된 조건 안에서만 자동으로 판단할
        수 있고, 조건이 깨지면 스스로 멈춥니다.</strong> 사기꾼처럼 위장 공격을 해보면서
        그 과정을 직접 확인해 보세요.
      </p>
      <p className="warn-banner">
        가상의 모의 공격입니다. 이 세션만을 위한 복제본을 공격하는 것이라 다른 방문자에게는
        아무 영향이 없어요.
      </p>

      <button className="send-button" onClick={attack} disabled={running}>
        {running ? "공격 중…" : "🎭 위장 공격 시작"}
      </button>

      {error && <p className="muted">{error}</p>}

      {story && (
        <>
          <ol className="story-steps">
            {visibleSteps.map((step, i) => (
              <li key={i} className={`story-step ${step.status}`}>
                <span className="story-icon">{step.icon}</span>
                <div className="story-body">
                  <strong>{step.title}</strong>
                  <p>{step.detail}</p>
                  {step.metric && <span className="story-metric">{step.metric}</span>}
                </div>
              </li>
            ))}
          </ol>

          {!showRecovery && story.demoted_at_window !== null && (
            <button className="send-button" onClick={() => setShowRecovery(true)}>
              🧑‍💼 사람이 확인했습니다 — 자동화 재승인
            </button>
          )}

          {showRecovery && story.recovered && (
            <p className="callout">
              ✅ 자동화가 복구됐습니다. 이 데모가 보여준 규칙 하나만 기억하세요:{" "}
              <strong>멈추는 건 시스템이 스스로, 다시 켜는 건 오직 사람이.</strong>
            </p>
          )}
        </>
      )}
    </section>
  );
}
